import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  clearPendingPlan,
  fetchBillingStatus,
  isBlocked,
  peekBillingStatus,
  readPendingPlan,
  startCheckout,
} from '../lib/billingFlow';

// Pages an account without a subscription may still open.
const OPEN_PATHS = ['/billing'];

/*
  Sits in front of every signed-in page. With billing switched on, an account
  that has not paid never lands in the workspace: a plan chosen at signup goes
  straight to Stripe Checkout, otherwise the person is sent to Billing to pick
  one. With billing off (no Stripe key on the server) it lets everything through.
*/
export default function BillingGate({ children }) {
  const { t } = useT();
  const location = useLocation();
  const [status, setStatus] = useState(() => peekBillingStatus());
  const [redirecting, setRedirecting] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchBillingStatus(API).then((s) => {
      if (!cancelled) setStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!status || started.current) return;
    const pending = readPendingPlan();
    if (!pending) return;
    if (!isBlocked(status)) {
      clearPendingPlan();
      return;
    }
    started.current = true;
    setRedirecting(true);
    startCheckout(API, pending.plan, pending.interval)
      .then(() => clearPendingPlan())
      .catch((e) => {
        clearPendingPlan();
        started.current = false;
        setRedirecting(false);
        toast.error(
          e?.response?.data?.detail || t('Could not open the payment page. Choose a plan to continue.')
        );
      });
  }, [status, t]);

  if (!status || redirecting) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-10 text-sm text-muted-foreground">
        {redirecting ? t('Opening secure payment…') : t('Loading…')}
      </div>
    );
  }
  if (isBlocked(status) && !OPEN_PATHS.includes(location.pathname)) {
    return <Navigate to="/billing" replace />;
  }
  return children;
}
